import { Badge, Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { IconButton } from '@/shared/ui';
import { SquareFunctionIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationAttrExplanationStep } from '@/entities/conversations';
import { useIsElementVisible } from '@/shared/hooks';
import { cn } from '@/shared/lib';
import { formatHumanDate } from '@/shared/lib/utils/date';

type Props = {
  /** Шаги разбора в порядке выполнения правила */
  steps: ConversationAttrExplanationStep[];
  /** Момент вычисления значения (ISO); null — не известен */
  computedAt?: string | null;
};

/**
 * Иконка разбора вычисления значения атрибута и всплывающее окно с его шагами.
 * Окно открывается по клику и закрывается явным действием — ни уход курсора, ни
 * прокрутка карточки его не закрывают, потому что разбор читают, сверяя с правилом
 * извлечения, а значения при этом листают.
 */
export const AttrExplanationPopover: FC<Props> = ({ steps, computedAt }) => {
  const { t, i18n } = useTranslation(['dialogs']);
  const [isOpen, setIsOpen] = useState(false);
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null);
  // Окно следует за иконкой, но рисуется поверх страницы, и край области значений его не
  // обрезает: за иконкой, уехавшей прокруткой за этот край, оно повисло бы над соседними
  // блоками. Поэтому, пока иконки не видно, окно спрятано, но остаётся открытым — вернувшись
  // к иконке, пользователь застаёт разбор на месте.
  // Прячется окно снятием с раскладки, а не невидимостью: оно лежит в прозрачной обёртке
  // позиционирования своего размера, и та, пока окно занимает место, перехватывает щелчки
  // и колесо над значениями. Щелчок вне окна по-прежнему его закрывает.
  const isTriggerVisible = useIsElementVisible(trigger, isOpen);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <IconButton
        ref={setTrigger}
        size="icon"
        className="size-5"
        label={t('dialogs:attrExplanation.iconLabel')}
        icon={<SquareFunctionIcon className="size-3.5 text-muted-foreground" />}
        trigger={PopoverTrigger}
      />
      {/*
        Высоту окна ограничивает и уводит остаток в прокрутку сам PopoverContent
        (`max-h` по свободному месту + `overflow-y-auto`). Здесь задаётся только
        ширина и внутренние отступы: снять ограничение высоты (`max-h-none`) или
        завернуть шаги в собственный контейнер с прокруткой нельзя — длинный
        разбор перестанет помещаться на экран.
      */}
      <PopoverContent className={cn('w-96 p-3', !isTriggerVisible && 'hidden')} align="end" closeOnScroll={false}>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">{t('dialogs:attrExplanation.title')}</span>
            {computedAt && (
              <span className="text-xs text-muted-foreground">
                {t('dialogs:attrExplanation.computedAt', {
                  value: formatHumanDate(i18n, computedAt, { withTime: true }),
                })}
              </span>
            )}
          </div>
          <ol className="flex flex-col gap-3">
            {steps.map((step) => (
              <li key={step.step} className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">
                  {t('dialogs:attrExplanation.step', { number: step.step })}
                </span>
                <span className="text-xs font-mono wrap-break-word whitespace-pre-wrap">{step.expression}</span>
                <span className="text-sm wrap-break-word whitespace-pre-wrap">{step.result}</span>
                {step.isTruncated && (
                  <Badge variant="secondary" outline className="self-start">
                    {t('dialogs:attrExplanation.truncated')}
                  </Badge>
                )}
              </li>
            ))}
          </ol>
        </div>
      </PopoverContent>
    </Popover>
  );
};
